import { useState, useEffect, useMemo } from 'react';
import { Bell, Clock, Calendar, Layers } from 'lucide-react';
import { timeToMinutes, formatReminderTime } from '@/lib/reminder-time';

interface OrientationPanelProps {
  hubName: string;
  reminders: any[];
  careMode: boolean;
  onToggleCareMode: () => void;
}

function getFormattedTime(date: Date): string {
  let h = date.getHours();
  const m = date.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function getWeekdayName(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

function getMonthDay(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

function getNextReminder(reminders: any[], now: Date): { title: string; time: string } | null {
  const todayName = getWeekdayName(now); // e.g. "Wednesday"
  const todayIso = now.toISOString().slice(0, 10);   // "YYYY-MM-DD"
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const todaysReminders = reminders.filter((r) => {
    if (!r.reminderTime) return false;
    const type = r.repeatType || 'never';

    if (type === 'daily') return true;

    if (type === 'weekly') {
      const days: string[] = Array.isArray(r.daysOfWeek) ? r.daysOfWeek : [];
      return days.some((d) => d.toLowerCase() === todayName.toLowerCase());
    }

    // One-time or monthly — check if startDate is today
    if (r.startDate) {
      const start = r.startDate.slice(0, 10);
      if (start === todayIso) return true;
    }

    return false;
  });

  const upcoming = todaysReminders
    .filter((r) => timeToMinutes(r.reminderTime) >= currentMinutes)
    .sort((a, b) => timeToMinutes(a.reminderTime) - timeToMinutes(b.reminderTime));

  if (upcoming.length === 0) return null;

  const next = upcoming[0];
  return { title: next.title, time: formatReminderTime(next.reminderTime) };
}

export function OrientationPanel({ hubName, reminders, careMode, onToggleCareMode }: OrientationPanelProps) {
  const [now, setNow] = useState(() => new Date());

  // Tick every minute
  useEffect(() => {
    const align = () => {
      setNow(new Date());
      const msUntilNextMinute = 60000 - (Date.now() % 60000);
      const timer = setTimeout(() => {
        setNow(new Date());
        const interval = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(interval);
      }, msUntilNextMinute);
      return timer;
    };
    const t = align();
    return () => clearTimeout(t);
  }, []);

  const timeStr = getFormattedTime(now);
  const dayStr = getWeekdayName(now);
  const dateStr = getMonthDay(now);
  const nextReminder = useMemo(() => getNextReminder(reminders, now), [reminders, now]);

  if (careMode) {
    return (
      <div
        className="sticky top-0 z-40 border-b border-amber-200 bg-white shadow-sm"
        role="banner"
        aria-label="Care mode orientation panel"
      >
        <div className="flex min-h-[76px] items-stretch">
          <div className="flex min-w-0 flex-1 items-center gap-4 bg-blue-600 px-5 py-3 text-white">
            <div className="min-w-0 flex-1">
              <span className="block text-xs font-semibold uppercase tracking-wider text-blue-100">My Hub</span>
              <span className="block truncate text-2xl font-bold">{hubName}</span>
            </div>
            <div className="shrink-0 text-right">
              <span className="block text-xs font-semibold uppercase tracking-wider text-blue-100">Now</span>
              <span className="block text-3xl font-bold tabular-nums">{timeStr}</span>
            </div>
          </div>
          <button
            onClick={onToggleCareMode}
            title="Switch to Standard Mode"
            aria-pressed="true"
            className="flex min-w-[96px] shrink-0 flex-col items-center justify-center gap-1 bg-yellow-400 px-4 py-3 font-bold text-yellow-950 transition-colors hover:bg-yellow-300"
          >
            <Layers className="h-7 w-7" />
            <span className="text-sm">Care</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="sticky top-0 z-40 bg-white border-b border-blue-100 shadow-sm"
      role="banner"
      aria-label="Orientation panel"
    >
      {/* ── Single row — all 5 segments in a flex row ──────────────────────────
          Mobile-first: tighter padding, smaller text, shorter labels.
          sm+ breakpoint: original generous sizing.                           */}
      <div className="flex items-stretch min-h-[56px] sm:min-h-[72px]">

        {/* Hub name */}
        <div className="flex flex-col items-center justify-center px-2 sm:px-5 py-2 sm:py-3 bg-blue-600 text-white w-[72px] sm:min-w-[120px] flex-shrink-0">
          <span className="text-[9px] sm:text-xs font-medium uppercase tracking-wide opacity-80 leading-none mb-0.5">Hub</span>
          <span className="text-sm sm:text-xl font-bold leading-tight text-center line-clamp-2">{hubName}</span>
        </div>

        {/* Day + Date — on mobile show only abbreviated day; full date on sm+ */}
        <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-2 sm:py-3 border-r border-blue-100 flex-1 min-w-0">
          <div className="flex items-center gap-1 text-blue-400 mb-0.5">
            <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
            <span className="text-[9px] sm:text-xs font-medium uppercase tracking-wide">Today</span>
          </div>
          {/* Mobile: abbreviated day only; sm+: full day name */}
          <span className="text-xs sm:text-lg font-bold text-gray-800 leading-tight truncate max-w-full">
            <span className="sm:hidden">{dayStr.slice(0, 3)}</span>
            <span className="hidden sm:inline">{dayStr}</span>
          </span>
          <span className="text-[10px] sm:text-xs text-gray-500 leading-tight truncate max-w-full">{dateStr}</span>
        </div>

        {/* Current time */}
        <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-2 sm:py-3 border-r border-blue-100 flex-1 min-w-0">
          <div className="flex items-center gap-1 text-blue-400 mb-0.5">
            <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
            <span className="text-[9px] sm:text-xs font-medium uppercase tracking-wide">Time</span>
          </div>
          <span className="text-base sm:text-2xl font-bold text-gray-800 leading-tight tabular-nums">{timeStr}</span>
        </div>

        {/* Next reminder */}
        <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-2 sm:py-3 border-r border-blue-100 flex-[2] min-w-0">
          <div className="flex items-center gap-1 text-blue-400 mb-0.5">
            <Bell className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
            <span className="text-[9px] sm:text-xs font-medium uppercase tracking-wide">Next</span>
          </div>
          {nextReminder ? (
            <>
              <span className="text-xs sm:text-base font-bold text-gray-800 leading-tight text-center line-clamp-1 w-full">{nextReminder.title}</span>
              <span className="text-[10px] sm:text-xs text-blue-600 font-medium leading-tight">{nextReminder.time}</span>
            </>
          ) : (
            <span className="text-[10px] sm:text-sm text-gray-400 text-center leading-tight">No reminders</span>
          )}
        </div>

        {/* Care Mode toggle */}
        <button
          onClick={onToggleCareMode}
          title={careMode ? 'Switch to Standard Mode' : 'Switch to Care Mode'}
          aria-pressed={careMode}
          className={`flex-shrink-0 flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-2 sm:px-5 py-2 sm:py-3 text-[9px] sm:text-xs font-bold transition-all border-l border-blue-100 min-w-[52px] sm:min-w-[80px] ${
            careMode
              ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-300'
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
          }`}
        >
          <Layers className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>{careMode ? 'Care' : 'Standard'}</span>
        </button>
      </div>
    </div>
  );
}
